import { Component, Inject, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { FootNotesService } from '../foot-notes.service';
import * as _ from 'lodash-es';

export interface CopyNoteDialogData {
  hoList: any[];
  formList: any[];
  scheduleList: any[];
  partners: any[];
  defaultSelections: {
    selectedFilterType: string;
    selectedHO: any;
    selectedForm: any;
    selectedSchedule: any;
    selectedPartner: any;
    hideSchedules: boolean;
    hidePartners: boolean;
  };
  activeNote: any;
  pdfEngineUrl: string;
  taxYear: string;
  scenario: string;
}

@Component({
  selector: 'gtw-copy-note-dialog',
  templateUrl: './copy-note-dialog.component.html',
  styleUrls: ['./copy-note-dialog.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CopyNoteDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  hoList: any[];
  formList: any[];
  scheduleList: any[];
  partners: any[];

  selectedFilterType: string;
  selectedHO: any;
  selectedForm: string;
  selectedSchedule: any;
  selectedPartners: any[] = [];
  hideSchedules: boolean;
  hidePartners: boolean;
  partnersLoading = false;
  minimised = false;
  hideCopy = false;

  state: { icon?: string; text?: string } = {};

  private defaults: CopyNoteDialogData['defaultSelections'];
  private activeNote: any;
  private pdfEngineUrl: string;
  private taxYear: string;
  private scenario: string;

  constructor(
    private dialogRef: MatDialogRef<CopyNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: CopyNoteDialogData,
    private footNotesService: FootNotesService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {
    this.hoList = data.hoList;
    this.formList = data.formList;
    this.scheduleList = [...data.scheduleList];
    this.partners = _.cloneDeep(data.partners);
    this.defaults = data.defaultSelections;
    this.activeNote = data.activeNote;
    this.pdfEngineUrl = data.pdfEngineUrl;
    this.taxYear = data.taxYear;
    this.scenario = data.scenario;

    const idx = this.partners.findIndex(
      p => p.leid !== null && p.leid === this.defaults.selectedPartner?.leid
    );
    if (idx > -1) {
      this.partners.splice(idx, 1);
    }

    this.selectedFilterType = this.defaults.selectedFilterType;
    this.selectedHO = _.cloneDeep(this.defaults.selectedHO);
    this.selectedForm = this.defaults.selectedForm?.form ?? '';
    this.selectedSchedule = this.defaults.selectedSchedule;
    this.hideSchedules = this.defaults.hideSchedules;
    this.hidePartners = this.defaults.hidePartners;

    if (this.defaults.selectedPartner?.leid === null) {
      this.selectedPartners = [{ leid: null }];
    }
  }

  partnerDropOpen = false;
  partnerSearch = '';

  compareHO = (a: any, b: any) => a?.value === b?.value;

  close(): void {
    this.dialogRef.close();
  }

  changeHO(): void {
    if (!this.selectedHO) return;
    const pieces = this.selectedHO.value.split('/');
    const rptPrd = this.selectedHO.name.substring(
      this.selectedHO.name.lastIndexOf('/') + 1,
      this.selectedHO.name.lastIndexOf(')')
    );
    this.selectedPartners = [];
    this.partnersLoading = true;

    this.footNotesService.getPartners(
      this.pdfEngineUrl, this.taxYear, this.scenario, pieces[0], pieces[1], rptPrd
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.partnersLoading = false;
        if (response.partnersList) {
          this.partners = response.partnersList;
          if (this.partners.length === 0 || (this.selectedForm && !this.selectedForm.toLowerCase().includes('sch'))) {
            this.hidePartners = true;
            this.selectedPartners = [{ leid: null }];
          } else {
            this.hidePartners = false;
            this.selectedPartners = [this.partners[0]];
          }
        }
        this.cdr.detectChanges();
      },
      () => {
        this.partnersLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  changeForm(): void {
    const formObj = this.formList.find(f => f.form === this.selectedForm);
    this.scheduleList = formObj?.schedules || [];
    if (this.scheduleList.length > 0) {
      this.selectedSchedule = this.scheduleList[0];
    }
    this.hideSchedules = !this.scheduleList.length || this.scheduleList[0] === null;

    if (this.selectedForm && !this.selectedForm.toLowerCase().includes('sch')) {
      this.hidePartners = true;
      this.selectedPartners = [{ leid: null }];
    } else if (this.partners.length > 0) {
      this.hidePartners = false;
      this.selectedPartners = [this.partners[0]];
    }
    this.cdr.detectChanges();
  }

  isPartnerSelected(partner: any): boolean {
    return this.selectedPartners.some(p => p.leid === partner.leid);
  }

  togglePartner(partner: any): void {
    const idx = this.selectedPartners.findIndex(p => p.leid === partner.leid);
    if (idx > -1) {
      this.selectedPartners.splice(idx, 1);
    } else {
      this.selectedPartners.push(partner);
    }
  }

  selectAllPartners(): void {
    this.selectedPartners = [...this.partners];
  }

  deselectAllPartners(): void {
    this.selectedPartners = [];
  }

  disableCopy(): boolean {
    if (!this.selectedHO || !this.selectedForm ||
        !this.selectedPartners || this.selectedPartners.length === 0 ||
        this.selectedFilterType === 'C') {
      return true;
    }
    if (
      this.selectedPartners.length === 1 &&
      this.selectedPartners[0].leid === null &&
      this.selectedFilterType === this.defaults.selectedFilterType &&
      this.selectedHO?.value === this.defaults.selectedHO?.value &&
      this.selectedForm === this.defaults.selectedForm?.form &&
      this.selectedSchedule === this.defaults.selectedSchedule
    ) {
      return true;
    }
    return false;
  }

  copy(): void {
    if (this.disableCopy()) return;

    if (this.activeNote.note_name === 'Untitled') {
      this._snackBar.open('Please name your footnote by double clicking on the title!', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.state = { icon: 'fa-spinner fa-spin', text: 'Copying...' };
    this.hideCopy = true;
    this.cdr.detectChanges();

    const rptPrd = this.selectedHO.name.substring(
      this.selectedHO.name.lastIndexOf('/') + 1,
      this.selectedHO.name.lastIndexOf(')')
    );

    const requests = this.selectedPartners.map(partner =>
      this.footNotesService.saveFootNote(
        this.pdfEngineUrl,
        null,
        this.activeNote.note_name,
        this.taxYear,
        this.scenario,
        this.selectedHO.me_code || '',
        this.selectedHO.value.split('/')[0],
        this.selectedHO.value.split('/')[1],
        rptPrd,
        partner.leid,
        this.selectedForm,
        this.selectedSchedule,
        this.activeNote.html_data,
        'N'
      )
    );

    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (responses: any[]) => {
        const anyFailed = responses.some(r => r.callSuccess !== '1');
        responses.forEach((r, i) => {
          if (r.callSuccess !== '1') {
            const name = this.selectedPartners[i]?.le_name || this.selectedHO.name;
            this._snackBar.open(`Failed to copy to ${name}!`, 'Close', {
              duration: 4000, panelClass: ['error-snackbar']
            });
          }
        });
        this.state = anyFailed
          ? { icon: 'fa-times-circle-o', text: 'Copy failed!' }
          : { icon: 'fa-check-circle', text: 'Copied Successfully!' };
        this.cdr.detectChanges();
      },
      () => {
        this.state = { icon: 'fa-times-circle-o', text: 'Copy failed!' };
        this.cdr.detectChanges();
      }
    );
  }
}
